import { frontConfig } from '@/config/front-config';
import axios, { AxiosError, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { pageNotFoundStore } from '@/store/pageNotFound';

type localStorageKey = 'accessToken' | 'refreshToken' | 'tokenSet';
type portal = 'admin-portal' | 'publisher-portal' | 'developer-portal';

const config = frontConfig();

export const getLocalStorage = (key: localStorageKey) => {
  return localStorage.getItem(key);
};

export const setLocalStorage = (key: localStorageKey, value: string) => {
  localStorage.setItem(key, value);
};

export const getAccessToken = () => {
  return Cookies.get('admin_portal_access_token') ?? '';
};

export const getRefreshToken = () => {
  return Cookies.get('admin_portal_refresh_token') ?? '';
};

export const removeTokenFromLocalStorage = (key: localStorageKey) => {
  localStorage.removeItem(key);
};

export const removeAccessTokenFromLocalStorage = () => {
  removeTokenFromLocalStorage('accessToken');
};

export const removeRefreshTokenFromLocalStorage = () => {
  removeTokenFromLocalStorage('refreshToken');
};

export const axiosClient = axios.create({
  baseURL: `${config.adminPortalBaseUrl}`,
});

export const refreshToken = async (clientRefreshToken: string) => {
  try {
    const {
      data: { refreshToken, accessToken },
    } = await axiosClient.put('/auth/refresh', {
      refreshToken: clientRefreshToken,
    });

    return {
      accessToken,
      refreshToken,
    };
  } catch (err) {
    throw new Error('Failed to refresh token');
  }
};

interface ExtendedResponse extends AxiosResponse {
  _retry: boolean;
}

axiosClient.interceptors.request.use((config) => {
  const token = getAccessToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// axiosClient.interceptors.response.use(
//   (response) => response.data,
//   async (err: AxiosError<AxiosResponse>) => {
//     const originalRequest = err.config as ExtendedResponse;
//     if (err.response?.status === 401 && !originalRequest._retry) {
//       console.log(`ATTEMPT TO REFRESH TOKEN`);
//       originalRequest._retry = true;
//       try {
//         const clientRefreshToken = getRefreshToken();
//         if (!clientRefreshToken) {
//           throw new Error('No refresh token available');
//         }
//         const decodedToken = jwtDecode(clientRefreshToken);
//         const currentTime = Math.floor(+new Date() / 1000);
//         const usRefreshTokenExpired = decodedToken.exp! - currentTime < 1;
//         if (usRefreshTokenExpired) {
//           alert('Session expired');
//           removeAccessTokenFromLocalStorage();
//           removeRefreshTokenFromLocalStorage();
//         }
//         const { accessToken, refreshToken: newRefreshToken } =
//           await refreshToken(clientRefreshToken);
//         setLocalStorage('accessToken', accessToken);
//         setLocalStorage('refreshToken', newRefreshToken);
//         axiosClient.defaults.headers.common.Authorization = `Bearer ${getAccessToken()}`;
//         originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
//         return axiosClient(originalRequest);
//       } catch (err: any) {
//         return Promise.reject(err);
//       }
//     }
//     return Promise.reject(err);
//   },
// );

// axiosClient.interceptors.response.use(
//   (response) => response.data,
//   async(error: AxiosError<AxiosResponse>) => {
//     const status = error.response?.status;
//     if (status === 401 || status === 402 || status === 403 || status === 502) {
//       window.location.href = '/unauthorize';
//     }

//     return Promise.reject(error);
//   }
// );

axiosClient.interceptors.response.use(
  (response) => response.data,
  async (error: AxiosError & { config?: any }) => {
    const status = error.response?.status;

    const isFrom404Page = pageNotFoundStore.getState().isFrom404Page;

    const skipRedirect = error.config?.skipUnauthorizedRedirect;

    if (!skipRedirect && !isFrom404Page && [401, 402, 403, 502].includes(status || 0)) {
      window.location.href = '/unauthorize';
    }

    return Promise.reject(error);
  }
);

export const getUserByUsername = async (username: string) => {
  try {
    const { data } = await axiosClient.get(`/users/${username}`);

    return data;
  } catch (err) {
    return {};
  }
};

export const  getUserRole = async (payload: {
  subject: string;
  action: string;
  object: string;
}) => {
  const { subject, action, object } = payload;
  const { data } = await axiosClient.get(
    `/police?subject=${subject}&action=${action}&object=${object}`,
  );

  return data;
};

export const getUserAccessByPortal = async (
  portal: portal,
  username: string,
): Promise<any> => {
  const subject = `net.mainapi:users:${username}`;
  const action = 'net.mainapi:actions:login';
  const object = `net.mainapi:services:${portal}`;

  const data = await getUserRole({ subject, action, object });
  return data;
};

export const checkIfUserPublisher = async (
  username: string,
): Promise<boolean> => {
  const publisherAccess = await getUserAccessByPortal(
    'publisher-portal',
    username,
  );
  const subject = `net.mainapi:users:${username}`;
  const action = 'net.mainapi:actions:login';
  const object = `net.mainapi:services:publisher-portal`;
  const hasAccess = Array.isArray(publisherAccess) &&
                    publisherAccess.length > 0 &&
                    typeof publisherAccess[0] === 'object' &&
                    'allow' in publisherAccess[0] &&
                    publisherAccess[0].allow === true &&
                    publisherAccess[0].subject === subject &&
                    publisherAccess[0].action === action &&
                    publisherAccess[0].object === object ;
  return hasAccess;
};

export const checkIfUserAdmin = async (username: string): Promise<boolean> => {
  const adminAccess = await getUserAccessByPortal('admin-portal', username);
  const subject = `net.mainapi:users:${username}`;
  const action = 'net.mainapi:actions:login';
  const object = `net.mainapi:services:admin-portal`;
  const hasAccess = Array.isArray(adminAccess) &&
                       adminAccess.length > 0 &&
                       typeof adminAccess[0] === 'object' &&
                       'allow' in adminAccess[0] &&
                       adminAccess[0].allow === true &&
                       adminAccess[0].subject === subject &&
                       adminAccess[0].action === action &&
                       adminAccess[0].object === object ;
  return hasAccess;
};

export const checkIfUserDeveloper = async (
  username: string,
): Promise<boolean> => {
  const adminAccess = await getUserAccessByPortal('developer-portal', username);
  const subject = `net.mainapi:users:${username}`;
  const action = 'net.mainapi:actions:login';
  const object = `net.mainapi:services:developer-portal`;
  const hasAccess = Array.isArray(adminAccess) &&
                       adminAccess.length > 0 &&
                       typeof adminAccess[0] === 'object' &&
                       'allow' in adminAccess[0] &&
                       adminAccess[0].allow === true &&
                       adminAccess[0].subject === subject &&
                       adminAccess[0].action === action &&
                       adminAccess[0].object === object ;
  return hasAccess;
};

export const whitelistUser = async (
  {
    username,
    active,
    allow,
  }: { username: string; active?: boolean; allow?: boolean },
  portal: string,
) => {
  try {
    const subject = `net.mainapi:users:${username}`;
    const action = 'net.mainapi:actions:login';
    const object = `net.mainapi:services:${portal}`;
    const { data } = await axiosClient.post('/police/enforce', {
      subject,
      action,
      object,
      active,
      allow,
    });
    return data;
  } catch (err: any) {
    if (err.response?.data?.message) {
      throw new Error(err.response?.data?.message);
    }
    throw new Error('Cannot whitelist user');
  }
};

export const updateUserStatus = async ({
  username,
  portal,
  allow,
}: {
  username: string;
  portal: string;
  allow: boolean;
}) => {
  try {
    const subject = `net.mainapi:users:${username}`;
    const action = 'net.mainapi:actions:login';
    const object = `net.mainapi:services:${portal}`;
    const { data } = await axiosClient.put('/police/user/status', {
      subject,
      action,
      object,
      allow,
    });
    return data;
  } catch (err: any) {
    if (err.response?.data?.message) {
      throw new Error(err.response?.data?.message);
    }
    throw new Error('Cannot update user status');
  }
};

export const updatePolicy = async ({
  username,
  portal,
  active,
  allow,
}: {
  username: string;
  portal: portal;
  active: boolean;
  allow: boolean;
}) => {
  try {
    const subject = `net.mainapi:users:${username}`;
    const action = 'net.mainapi:actions:login';
    const object = `net.mainapi:services:${portal}`;
    const { data: updatedPolicy } = await axiosClient.put('/police', {
      subject,
      action,
      object,
      active,
      allow,
    });
    return {
      updatedPolicy,
    };
  } catch (err: any) {
    if (err.response?.data?.message) {
      throw new Error(err.response?.data?.message);
    }
    throw new Error('Cannot update user status');
  }
};

export const verifyUserPassword = async (email: string, password: string) => {
  if (!email || !password) {
    throw new Error('Email or password is empty');
  }
  
  try {
    const user = await axiosClient.post('/auth/credentials', {
      email,
      password,
    });

    return user;
  } catch (err: any) {
    if (err.response?.data?.message) {
      throw new Error(err.response?.data?.message);
    }
    throw new Error('Cannot update user status');
  }
};
